import { reportingAPI } from '../services/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface ExportOptions {
  reportType: string;
  quizId?: number;
  format: 'json' | 'csv' | 'pdf';
  timeRange?: any;
  filename?: string;
}

// Convert object data to CSV format
const convertToCSV = (data: any[], headers?: string[]): string => {
  if (!data || data.length === 0) return '';
  
  // If headers not provided, use keys from first object
  const csvHeaders = headers || Object.keys(data[0]);
  
  // Create header row
  const headerRow = csvHeaders.join(',');
  
  // Create data rows
  const dataRows = data.map(item => {
    return csvHeaders.map(header => {
      const value = item[header];
      // Handle nested objects and arrays
      if (typeof value === 'object' && value !== null) {
        return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
      }
      // Escape quotes and wrap in quotes if contains comma
      const stringValue = String(value || '');
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    }).join(',');
  });
  
  return [headerRow, ...dataRows].join('\n');
};

// Generate CSV for different report types
const generateReportCSV = (reportType: string, data: any): string => {
  switch (reportType) {
    case 'system_wide':
      return generateSystemWideCSV(data);
    case 'quiz_summary':
      return generateQuizSummaryCSV(data);
    case 'quiz_detailed':
      return generateQuizDetailedCSV(data);
    case 'participant_performance':
      return generateParticipantCSV(data);
    case 'category_performance':
      return generateCategoryCSV(data);
    case 'overall_question_performance':
      return generateOverallQuestionPerformanceCSV(data);
    case 'quiz_question_performance':
      return generateQuizQuestionPerformanceCSV(data);
    default:
      return JSON.stringify(data, null, 2);
  }
};

const generateSystemWideCSV = (data: any): string => {
  const systemStats = [{
    total_quizzes: data.total_quizzes,
    total_responses: data.total_responses,
    total_completed_responses: data.total_completed_responses,
    total_participants: data.total_participants,
    average_quiz_score: data.average_quiz_score,
    most_popular_category: data.most_popular_category,
    busiest_day: data.busiest_day
  }];
  
  let csv = 'System Overview\n';
  csv += convertToCSV(systemStats) + '\n\n';
  
  if (data.quiz_performance_by_category && data.quiz_performance_by_category.length > 0) {
    csv += 'Category Performance\n';
    csv += convertToCSV(data.quiz_performance_by_category) + '\n\n';
  }
  
  if (data.recent_activity && data.recent_activity.length > 0) {
    csv += 'Recent Activity\n';
    csv += convertToCSV(data.recent_activity);
  }
  
  return csv;
};

const generateQuizSummaryCSV = (data: any): string => {
  const summary = [{
    quiz_title: data.quiz_title,
    quiz_category: data.quiz_category,
    total_responses: data.total_responses,
    completed_responses: data.completed_responses,
    incomplete_responses: data.incomplete_responses,
    average_score: data.average_score,
    average_completion_time_minutes: data.average_completion_time_minutes,
    first_response_date: data.first_response_date,
    last_response_date: data.last_response_date
  }];
  
  return convertToCSV(summary);
};

const generateQuizDetailedCSV = (data: any): string => {
  let csv = `Quiz Report: ${data.quiz_title}\n\n`;
  
  // Quiz overview
  const overview = [{
    quiz_title: data.quiz_title,
    quiz_category: data.quiz_category,
    total_questions: data.total_questions,
    total_responses: data.total_responses,
    completed_responses: data.completed_responses,
    average_score: data.average_score,
    median_score: data.median_score,
    highest_score: data.highest_score,
    lowest_score: data.lowest_score
  }];
  
  csv += 'Quiz Overview\n';
  csv += convertToCSV(overview) + '\n\n';
  
  // Question statistics
  if (data.question_stats && data.question_stats.length > 0) {
    csv += 'Question Performance\n';
    csv += convertToCSV(data.question_stats) + '\n\n';
  }
  
  // Response trends
  if (data.response_trends && data.response_trends.length > 0) {
    csv += 'Response Trends\n';
    csv += convertToCSV(data.response_trends) + '\n\n';
  }
  
  // Participant statistics
  if (data.participant_stats && data.participant_stats.length > 0) {
    csv += 'Participant Statistics\n';
    csv += convertToCSV(data.participant_stats);
  }
  
  return csv;
};

const generateParticipantCSV = (data: any[]): string => {
  if (!Array.isArray(data)) return '';
  
  const participantData = data.map(participant => ({
    participant_name: participant.participant_name,
    participant_email: participant.participant_email,
    completed_at: participant.completed_at,
    score_percentage: participant.score_percentage,
    completion_time_minutes: participant.completion_time_minutes,
    correct_answers: participant.correct_answers,
    incorrect_answers: participant.incorrect_answers,
    partial_answers: participant.partial_answers
  }));
  
  return convertToCSV(participantData);
};

const generateCategoryCSV = (data: any[]): string => {
  if (!Array.isArray(data)) return '';
  
  return convertToCSV(data);
};

const generateQuizQuestionPerformanceCSV = (data: any): string => {
  if (!data) return '';
  
  let csv = `Quiz Question Performance Report\n`;
  csv += `Quiz: ${data.quiz_title}\n`;
  if (data.quiz_category) {
    csv += `Category: ${data.quiz_category}\n`;
  }
  csv += `\nOverall Statistics\n`;
  csv += convertToCSV([{
    total_questions_answered: data.total_questions_answered,
    total_correct: data.total_correct,
    total_incorrect: data.total_incorrect,
    total_partial: data.total_partial,
    correct_percentage: `${data.correct_percentage}%`,
    incorrect_percentage: `${data.incorrect_percentage}%`,
    partial_percentage: `${data.partial_percentage}%`
  }]);
  
  if (data.question_performance && data.question_performance.length > 0) {
    csv += `\n\nQuestion Performance\n`;
    csv += convertToCSV(data.question_performance.map((q: any) => ({
      question_order: `Q${q.question_order}`,
      question_text: q.question_text,
      question_type: q.question_type,
      difficulty_level: q.difficulty_level,
      total_answers: q.total_answers,
      correct_answers: q.correct_answers,
      incorrect_answers: q.incorrect_answers,
      partial_answers: q.partial_answers,
      correct_percentage: `${q.correct_percentage}%`,
      incorrect_percentage: `${q.incorrect_percentage}%`,
      partial_percentage: `${q.partial_percentage}%`,
      average_accuracy: `${q.average_accuracy}%`
    })));
  }
  
  return csv;
};

const generateOverallQuestionPerformanceCSV = (data: any): string => {
  if (!data) return '';
  
  let csv = 'Overall Question Performance Report\n\n';
  
  // Overall statistics
  csv += 'Overall Statistics\n';
  csv += convertToCSV([{
    total_questions_answered: data.total_questions_answered,
    total_correct: data.total_correct,
    total_incorrect: data.total_incorrect,
    total_partial: data.total_partial,
    correct_percentage: data.correct_percentage,
    incorrect_percentage: data.incorrect_percentage,
    partial_percentage: data.partial_percentage
  }]) + '\n\n';
  
  // Topic performance
  if (data.topic_performance && data.topic_performance.length > 0) {
    csv += 'Performance by Topic/Category\n';
    csv += convertToCSV(data.topic_performance, [
      'category',
      'difficulty_level',
      'total_questions_answered',
      'correct_answers',
      'incorrect_answers',
      'partial_answers',
      'correct_percentage',
      'incorrect_percentage',
      'partial_percentage',
      'average_accuracy'
    ]);
  }
  
  return csv;
};

// Download file utility
const downloadFile = (content: string, filename: string, contentType: string) => {
  const blob = new Blob([content], { type: contentType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Generate PDF content (basic HTML to PDF conversion)
export const generatePDFContent = (reportType: string, data: any, reportName: string): string => {
  const currentDate = new Date().toLocaleDateString();
  
  let html = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>${reportName}</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 10px; }
        .section { margin-bottom: 25px; }
        .section h2 { color: #333; border-bottom: 1px solid #ccc; padding-bottom: 5px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #f2f2f2; font-weight: bold; }
        .stat-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px; margin-bottom: 20px; }
        .stat-card { border: 1px solid #ddd; padding: 15px; text-align: center; }
        .stat-value { font-size: 24px; font-weight: bold; color: #007bff; }
        .stat-label { color: #666; font-size: 12px; margin-top: 5px; }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>${reportName}</h1>
        <p>Generated on ${currentDate}</p>
      </div>
  `;
  
  switch (reportType) {
    case 'system_wide':
      html += generateSystemWidePDF(data);
      break;
    case 'quiz_summary':
    case 'quiz_detailed':
      html += generateQuizPDF(data, reportType === 'quiz_detailed');
      break;
    case 'participant_performance':
      html += generateParticipantPDF(data);
      break;
    case 'category_performance':
      html += generateCategoryPDF(data);
      break;
    case 'overall_question_performance':
      // This case is handled separately in exportReport function using jsPDF
      html += `<pre>${JSON.stringify(data, null, 2)}</pre>`;
      break;
    default:
      html += `<pre>${JSON.stringify(data, null, 2)}</pre>`;
  }
  
  html += '</body></html>';
  return html;
};

const generateSystemWidePDF = (data: any): string => {
  return `
    <div class="section">
      <div class="stat-grid">
        <div class="stat-card">
          <div class="stat-value">${data.total_quizzes}</div>
          <div class="stat-label">Total Quizzes</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">${data.total_responses}</div>
          <div class="stat-label">Total Responses</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">${data.total_completed_responses}</div>
          <div class="stat-label">Completed Responses</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">${data.total_participants}</div>
          <div class="stat-label">Unique Participants</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">${Math.round(data.average_quiz_score)}%</div>
          <div class="stat-label">Average Score</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">${data.most_popular_category || 'N/A'}</div>
          <div class="stat-label">Most Popular Category</div>
        </div>
      </div>
    </div>
    
    ${data.quiz_performance_by_category && data.quiz_performance_by_category.length > 0 ? `
      <div class="section">
        <h2>Category Performance</h2>
        <table>
          <tr><th>Category</th><th>Quiz Count</th><th>Total Responses</th><th>Average Score</th></tr>
          ${data.quiz_performance_by_category.map((cat: any) => 
            `<tr><td>${cat.category}</td><td>${cat.quiz_count}</td><td>${cat.total_responses}</td><td>${Math.round(cat.average_score)}%</td></tr>`
          ).join('')}
        </table>
      </div>
    ` : ''}
  `;
};

const generateQuizPDF = (data: any, detailed: boolean): string => {
  let html = `
    <div class="section">
      <h2>${data.quiz_title}</h2>
      <div class="stat-grid">
        <div class="stat-card">
          <div class="stat-value">${data.total_responses || data.total_questions || 0}</div>
          <div class="stat-label">${detailed ? 'Total Questions' : 'Total Responses'}</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">${data.completed_responses || 0}</div>
          <div class="stat-label">Completed Responses</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">${Math.round(data.average_score || 0)}%</div>
          <div class="stat-label">Average Score</div>
        </div>
      </div>
    </div>
  `;
  
  if (detailed && data.question_stats && data.question_stats.length > 0) {
    html += `
      <div class="section">
        <h2>Question Performance</h2>
        <table>
          <tr><th>Question</th><th>Type</th><th>Accuracy</th><th>Total Answers</th><th>Correct</th></tr>
          ${data.question_stats.map((q: any, index: number) => 
            `<tr>
              <td>Q${index + 1}: ${q.question_text.substring(0, 50)}...</td>
              <td>${q.question_type}</td>
              <td>${q.accuracy_percentage}%</td>
              <td>${q.total_answers}</td>
              <td>${q.correct_answers}</td>
            </tr>`
          ).join('')}
        </table>
      </div>
    `;
  }
  
  return html;
};

const generateParticipantPDF = (data: any[]): string => {
  if (!Array.isArray(data)) return '';
  
  return `
    <div class="section">
      <h2>Participant Performance</h2>
      <table>
        <tr>
          <th>Participant</th>
          <th>Email</th>
          <th>Score</th>
          <th>Completion Time</th>
          <th>Correct</th>
          <th>Incorrect</th>
        </tr>
        ${data.map(p => 
          `<tr>
            <td>${p.participant_name}</td>
            <td>${p.participant_email}</td>
            <td>${p.score_percentage ? Math.round(p.score_percentage) + '%' : 'N/A'}</td>
            <td>${p.completion_time_minutes ? Math.round(p.completion_time_minutes) + ' min' : 'N/A'}</td>
            <td>${p.correct_answers}</td>
            <td>${p.incorrect_answers}</td>
          </tr>`
        ).join('')}
      </table>
    </div>
  `;
};

const generateCategoryPDF = (data: any[]): string => {
  if (!Array.isArray(data)) return '';
  
  return `
    <div class="section">
      <h2>Category Performance</h2>
      <table>
        <tr><th>Category</th><th>Quiz Count</th><th>Total Responses</th><th>Average Score</th></tr>
        ${data.map(cat => 
          `<tr><td>${cat.category}</td><td>${cat.quiz_count}</td><td>${cat.total_responses}</td><td>${Math.round(cat.average_score)}%</td></tr>`
        ).join('')}
      </table>
    </div>
  `;
};

// Main export function
export const exportReport = async (options: ExportOptions): Promise<void> => {
  try {
    // Get report data
    const response = await reportingAPI.exportReport(options.reportType, options.quizId, options.timeRange);
    const data = response.data.data;
    const metadata = response.data.metadata;
    
    // Generate filename
    const timestamp = new Date().toISOString().split('T')[0];
    const baseFilename = options.filename || metadata.report_name || 'report';
    const filename = `${baseFilename}_${timestamp}`;
    
    // Export based on format
    switch (options.format) {
      case 'csv':
        const csvContent = generateReportCSV(options.reportType, data);
        downloadFile(csvContent, `${filename}.csv`, 'text/csv');
        break;
        
      case 'pdf':
        if (options.reportType === 'overall_question_performance') {
          await generateOverallQuestionPerformancePDF(data, metadata.report_name || 'Report', filename);
        } else if (options.reportType === 'quiz_question_performance') {
          await generateQuizQuestionPerformancePDF(data, metadata.report_name || 'Report', filename);
        } else {
          const pdfContent = generatePDFContent(options.reportType, data, metadata.report_name || 'Report');
          downloadFile(pdfContent, `${filename}.html`, 'text/html');
        }
        break;
        
      case 'json':
      default:
        const jsonContent = JSON.stringify(data, null, 2);
        downloadFile(jsonContent, `${filename}.json`, 'application/json');
        break;
    }
  } catch (error) {
    console.error('Export failed:', error);
    throw error;
  }
};

// Generate PDF for overall question performance using jsPDF
const generateOverallQuestionPerformancePDF = async (
  data: any,
  reportName: string,
  filename: string
): Promise<void> => {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = { left: 15, right: 15, top: 15, bottom: 20 };
  let y = margin.top;

  // Professional Header with colored bar
  pdf.setFillColor(36, 67, 118);
  pdf.rect(0, 0, pageWidth, 25, 'F');
  
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(20);
  pdf.setTextColor(255, 255, 255);
  pdf.text('Overall Question Performance Report', pageWidth / 2, 12, { align: 'center' });
  
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(220, 220, 220);
  pdf.text(`Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, pageWidth / 2, 18, { align: 'center' });
  
  y = 35;

  // Overall Statistics Section with better design
  pdf.setFillColor(248, 249, 251);
  pdf.rect(margin.left, y - 5, pageWidth - margin.left - margin.right, 8, 'F');
  
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(16);
  pdf.setTextColor(36, 67, 118);
  pdf.text('Overall Statistics', margin.left + 3, y);
  y += 12;

  // Overall stats cards with better spacing and design
  const cardWidth = (pageWidth - margin.left - margin.right - 16) / 3;
  const cardHeight = 35;
  let x = margin.left;

  // Correct Answers Card - Enhanced design
  pdf.setFillColor(240, 253, 244);
  pdf.setDrawColor(40, 167, 69);
  pdf.setLineWidth(1);
  pdf.roundedRect(x, y, cardWidth, cardHeight, 3, 3, 'FD');
  
  // Icon circle with checkmark
  pdf.setFillColor(40, 167, 69);
  pdf.circle(x + cardWidth / 2, y + 8, 4, 'F');
  // Draw checkmark manually
  pdf.setDrawColor(255, 255, 255);
  pdf.setLineWidth(1.5);
  pdf.setLineCap('round');
  pdf.line(x + cardWidth / 2 - 2, y + 8, x + cardWidth / 2 - 0.5, y + 9.5);
  pdf.line(x + cardWidth / 2 - 0.5, y + 9.5, x + cardWidth / 2 + 2, y + 6.5);
  
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(24);
  pdf.setTextColor(40, 167, 69);
  pdf.text(`${data.correct_percentage}%`, x + cardWidth / 2, y + 20, { align: 'center' });
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(33, 37, 41);
  pdf.text('Correct Answers', x + cardWidth / 2, y + 25, { align: 'center' });
  pdf.setFontSize(8);
  pdf.setTextColor(108, 117, 125);
  pdf.text(`${data.total_correct.toLocaleString()} of ${data.total_questions_answered.toLocaleString()}`, x + cardWidth / 2, y + 30, { align: 'center' });
  x += cardWidth + 8;

  // Wrong Answers Card - Enhanced design
  pdf.setFillColor(255, 245, 245);
  pdf.setDrawColor(220, 53, 69);
  pdf.roundedRect(x, y, cardWidth, cardHeight, 3, 3, 'FD');
  
  // Icon circle with X
  pdf.setFillColor(220, 53, 69);
  pdf.circle(x + cardWidth / 2, y + 8, 4, 'F');
  // Draw X manually
  pdf.setDrawColor(255, 255, 255);
  pdf.setLineWidth(1.5);
  pdf.setLineCap('round');
  pdf.line(x + cardWidth / 2 - 2, y + 6.5, x + cardWidth / 2 + 2, y + 9.5);
  pdf.line(x + cardWidth / 2 + 2, y + 6.5, x + cardWidth / 2 - 2, y + 9.5);
  
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(24);
  pdf.setTextColor(220, 53, 69);
  pdf.text(`${data.incorrect_percentage}%`, x + cardWidth / 2, y + 20, { align: 'center' });
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(33, 37, 41);
  pdf.text('Wrong Answers', x + cardWidth / 2, y + 25, { align: 'center' });
  pdf.setFontSize(8);
  pdf.setTextColor(108, 117, 125);
  pdf.text(`${data.total_incorrect.toLocaleString()} of ${data.total_questions_answered.toLocaleString()}`, x + cardWidth / 2, y + 30, { align: 'center' });
  x += cardWidth + 8;

  // Partial Answers Card - Enhanced design
  pdf.setFillColor(255, 254, 245);
  pdf.setDrawColor(255, 193, 7);
  pdf.roundedRect(x, y, cardWidth, cardHeight, 3, 3, 'FD');
  
  // Icon circle
  pdf.setFillColor(255, 193, 7);
  pdf.circle(x + cardWidth / 2, y + 8, 4, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.text('~', x + cardWidth / 2, y + 10.5, { align: 'center' });
  
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(24);
  pdf.setTextColor(255, 193, 7);
  pdf.text(`${data.partial_percentage}%`, x + cardWidth / 2, y + 20, { align: 'center' });
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(33, 37, 41);
  pdf.text('Partially Correct', x + cardWidth / 2, y + 25, { align: 'center' });
  pdf.setFontSize(8);
  pdf.setTextColor(108, 117, 125);
  pdf.text(`${data.total_partial.toLocaleString()} of ${data.total_questions_answered.toLocaleString()}`, x + cardWidth / 2, y + 30, { align: 'center' });
  y += cardHeight + 20;

  // Topic Performance Section
  if (data.topic_performance && data.topic_performance.length > 0) {
    // Check if we need a new page
    if (y > pageHeight - 80) {
      pdf.addPage();
      y = margin.top + 10;
    }

    // Section header with background
    pdf.setFillColor(248, 249, 251);
    pdf.rect(margin.left, y - 5, pageWidth - margin.left - margin.right, 8, 'F');
    
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.setTextColor(36, 67, 118);
    pdf.text('Performance by Topic/Category', margin.left + 3, y);
    y += 10;

    pdf.setFont('helvetica', 'italic');
    pdf.setFontSize(9);
    pdf.setTextColor(108, 117, 125);
    const descText = 'Topics are sorted by difficulty level. Red indicates difficult topics where trainees struggle most.';
    const descLines = pdf.splitTextToSize(descText, pageWidth - margin.left - margin.right);
    descLines.forEach((line: string, idx: number) => {
      pdf.text(line, margin.left, y + idx * 4);
    });
    y += descLines.length * 4 + 8;

    // Topic performance table with better styling
    const tableData = data.topic_performance.map((topic: any) => {
      return [
        topic.category,
        topic.difficulty_level.charAt(0).toUpperCase() + topic.difficulty_level.slice(1),
        topic.total_questions_answered.toString(),
        `${topic.correct_percentage}%`,
        `${topic.incorrect_percentage}%`,
        `${topic.partial_percentage}%`,
        `${topic.average_accuracy}%`
      ];
    });

    autoTable(pdf, {
      startY: y,
      head: [['Category', 'Difficulty', 'Total', 'Correct %', 'Wrong %', 'Partial %', 'Avg Accuracy']],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [36, 67, 118],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 11,
        halign: 'center'
      },
      bodyStyles: {
        fontSize: 10,
        textColor: [33, 37, 41],
        cellPadding: 4
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250]
      },
      columnStyles: {
        0: { cellWidth: 45, fontStyle: 'bold', halign: 'left' },
        1: { cellWidth: 28, halign: 'center', fontStyle: 'bold' },
        2: { cellWidth: 18, halign: 'center' },
        3: { cellWidth: 22, halign: 'center', textColor: [40, 167, 69], fontStyle: 'bold' },
        4: { cellWidth: 22, halign: 'center', textColor: [220, 53, 69], fontStyle: 'bold' },
        5: { cellWidth: 22, halign: 'center', textColor: [255, 193, 7], fontStyle: 'bold' },
        6: { cellWidth: 25, halign: 'center', fontStyle: 'bold', textColor: [36, 67, 118] }
      },
      margin: { left: margin.left, right: margin.right },
      didParseCell: (cellData: any) => {
        if (cellData.row.index > 0) {
          const topic = data.topic_performance[cellData.row.index - 1];
          
          // Color entire row based on difficulty
          if (topic.difficulty_level === 'difficult') {
            cellData.cell.styles.fillColor = [255, 245, 245];
          } else if (topic.difficulty_level === 'moderate') {
            cellData.cell.styles.fillColor = [255, 254, 245];
          } else {
            cellData.cell.styles.fillColor = [245, 255, 245];
          }
          
          // Style difficulty column
          if (cellData.column.index === 1) {
            if (topic.difficulty_level === 'difficult') {
              cellData.cell.styles.textColor = [220, 53, 69];
            } else if (topic.difficulty_level === 'moderate') {
              cellData.cell.styles.textColor = [255, 193, 7];
            } else {
              cellData.cell.styles.textColor = [40, 167, 69];
            }
          }
        }
      }
    });

    y = (pdf as any).lastAutoTable?.finalY || y;
    y += 15;

    // Key Insights Section
    if (y > pageHeight - 90) {
      pdf.addPage();
      y = margin.top + 10;
    }

    // Section header with background
    pdf.setFillColor(248, 249, 251);
    pdf.rect(margin.left, y - 5, pageWidth - margin.left - margin.right, 8, 'F');
    
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.setTextColor(36, 67, 118);
    pdf.text('Key Insights', margin.left + 3, y);
    y += 12;

    const difficultTopics = data.topic_performance
      .filter((t: any) => t.difficulty_level === 'difficult')
      .slice(0, 5);
    const easyTopics = data.topic_performance
      .filter((t: any) => t.difficulty_level === 'easy')
      .slice(0, 5);

    const insightsWidth = (pageWidth - margin.left - margin.right - 12) / 2;
    let insightsX = margin.left;
    const insightsHeight = Math.max(45, Math.max(difficultTopics.length, easyTopics.length) * 7 + 20);

    // Most Difficult Topics - Enhanced design
    pdf.setFillColor(255, 245, 245);
    pdf.setDrawColor(220, 53, 69);
    pdf.setLineWidth(1);
    pdf.roundedRect(insightsX, y, insightsWidth, insightsHeight, 4, 4, 'FD');
    
    // Header bar
    pdf.setFillColor(220, 53, 69);
    pdf.roundedRect(insightsX, y, insightsWidth, 10, 4, 4, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.setTextColor(255, 255, 255);
    pdf.text('Most Difficult Topics', insightsX + insightsWidth / 2, y + 6.5, { align: 'center' });
    
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(33, 37, 41);
    let insightsY = y + 18;
    if (difficultTopics.length > 0) {
      difficultTopics.forEach((topic: any, idx: number) => {
        // Draw bullet point manually
        pdf.setFillColor(220, 53, 69);
        pdf.circle(insightsX + 5, insightsY - 1, 1, 'F');
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(33, 37, 41);
        pdf.text(`${topic.category}`, insightsX + 10, insightsY);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(108, 117, 125);
        pdf.text(`${topic.average_accuracy}% accuracy`, insightsX + insightsWidth - 5, insightsY, { align: 'right' });
        insightsY += 7;
      });
    } else {
      pdf.setTextColor(108, 117, 125);
      pdf.text('No topics marked as difficult', insightsX + 5, insightsY);
    }
    insightsX += insightsWidth + 12;

    // Easiest Topics - Enhanced design
    pdf.setFillColor(245, 255, 245);
    pdf.setDrawColor(40, 167, 69);
    pdf.roundedRect(insightsX, y, insightsWidth, insightsHeight, 4, 4, 'FD');
    
    // Header bar
    pdf.setFillColor(40, 167, 69);
    pdf.roundedRect(insightsX, y, insightsWidth, 10, 4, 4, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.setTextColor(255, 255, 255);
    pdf.text('Easiest Topics', insightsX + insightsWidth / 2, y + 6.5, { align: 'center' });
    
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(33, 37, 41);
    insightsY = y + 18;
    if (easyTopics.length > 0) {
      easyTopics.forEach((topic: any) => {
        // Draw bullet point manually
        pdf.setFillColor(40, 167, 69);
        pdf.circle(insightsX + 5, insightsY - 1, 1, 'F');
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(33, 37, 41);
        pdf.text(`${topic.category}`, insightsX + 10, insightsY);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(108, 117, 125);
        pdf.text(`${topic.average_accuracy}% accuracy`, insightsX + insightsWidth - 5, insightsY, { align: 'right' });
        insightsY += 7;
      });
    } else {
      pdf.setTextColor(108, 117, 125);
      pdf.text('No topics marked as easy', insightsX + 5, insightsY);
    }
  }

  // Professional Footer
  const pageCount = (pdf.internal as any).getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    
    // Footer line
    pdf.setDrawColor(220, 220, 220);
    pdf.setLineWidth(0.5);
    pdf.line(margin.left, pageHeight - 15, pageWidth - margin.right, pageHeight - 15);
    
    pdf.setFontSize(9);
    pdf.setTextColor(108, 117, 125);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Roslin Quiz System', margin.left, pageHeight - 8);
    pdf.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
    
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(36, 67, 118);
    const footerText = reportName.length > 40 ? reportName.substring(0, 37) + '...' : reportName;
    pdf.text(footerText, pageWidth - margin.right, pageHeight - 8, { align: 'right' });
  }

  // Save PDF
  pdf.save(`${filename}.pdf`);
};

// Generate PDF for quiz question performance using jsPDF
const generateQuizQuestionPerformancePDF = async (
  data: any,
  reportName: string,
  filename: string
): Promise<void> => {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = { left: 15, right: 15, top: 15, bottom: 20 };
  let y = margin.top;

  // Professional Header with colored bar
  pdf.setFillColor(36, 67, 118);
  pdf.rect(0, 0, pageWidth, 25, 'F');
  
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(18);
  pdf.setTextColor(255, 255, 255);
  pdf.text('Quiz Question Performance Report', pageWidth / 2, 12, { align: 'center' });
  
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(220, 220, 220);
  pdf.text(data.quiz_title || 'Quiz Report', pageWidth / 2, 18, { align: 'center' });
  
  y = 35;

  // Overall Statistics Section
  pdf.setFillColor(248, 249, 251);
  pdf.rect(margin.left, y - 5, pageWidth - margin.left - margin.right, 8, 'F');
  
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(16);
  pdf.setTextColor(36, 67, 118);
  pdf.text('Overall Statistics', margin.left + 3, y);
  y += 12;

  // Overall stats cards
  const cardWidth = (pageWidth - margin.left - margin.right - 16) / 3;
  const cardHeight = 35;
  let x = margin.left;

  // Correct Answers Card
  pdf.setFillColor(240, 253, 244);
  pdf.setDrawColor(40, 167, 69);
  pdf.setLineWidth(1);
  pdf.roundedRect(x, y, cardWidth, cardHeight, 3, 3, 'FD');
  pdf.setFillColor(40, 167, 69);
  pdf.circle(x + cardWidth / 2, y + 8, 4, 'F');
  pdf.setDrawColor(255, 255, 255);
  pdf.setLineWidth(1.5);
  pdf.setLineCap('round');
  pdf.line(x + cardWidth / 2 - 2, y + 8, x + cardWidth / 2 - 0.5, y + 9.5);
  pdf.line(x + cardWidth / 2 - 0.5, y + 9.5, x + cardWidth / 2 + 2, y + 6.5);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(24);
  pdf.setTextColor(40, 167, 69);
  pdf.text(`${data.correct_percentage}%`, x + cardWidth / 2, y + 20, { align: 'center' });
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(33, 37, 41);
  pdf.text('Correct Answers', x + cardWidth / 2, y + 25, { align: 'center' });
  pdf.setFontSize(8);
  pdf.setTextColor(108, 117, 125);
  pdf.text(`${data.total_correct.toLocaleString()} of ${data.total_questions_answered.toLocaleString()}`, x + cardWidth / 2, y + 30, { align: 'center' });
  x += cardWidth + 8;

  // Wrong Answers Card
  pdf.setFillColor(255, 245, 245);
  pdf.setDrawColor(220, 53, 69);
  pdf.roundedRect(x, y, cardWidth, cardHeight, 3, 3, 'FD');
  pdf.setFillColor(220, 53, 69);
  pdf.circle(x + cardWidth / 2, y + 8, 4, 'F');
  pdf.setDrawColor(255, 255, 255);
  pdf.setLineWidth(1.5);
  pdf.setLineCap('round');
  pdf.line(x + cardWidth / 2 - 2, y + 6.5, x + cardWidth / 2 + 2, y + 9.5);
  pdf.line(x + cardWidth / 2 + 2, y + 6.5, x + cardWidth / 2 - 2, y + 9.5);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(24);
  pdf.setTextColor(220, 53, 69);
  pdf.text(`${data.incorrect_percentage}%`, x + cardWidth / 2, y + 20, { align: 'center' });
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(33, 37, 41);
  pdf.text('Wrong Answers', x + cardWidth / 2, y + 25, { align: 'center' });
  pdf.setFontSize(8);
  pdf.setTextColor(108, 117, 125);
  pdf.text(`${data.total_incorrect.toLocaleString()} of ${data.total_questions_answered.toLocaleString()}`, x + cardWidth / 2, y + 30, { align: 'center' });
  x += cardWidth + 8;

  // Partial Answers Card
  pdf.setFillColor(255, 254, 245);
  pdf.setDrawColor(255, 193, 7);
  pdf.roundedRect(x, y, cardWidth, cardHeight, 3, 3, 'FD');
  pdf.setFillColor(255, 193, 7);
  pdf.circle(x + cardWidth / 2, y + 8, 4, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.setTextColor(255, 255, 255);
  pdf.text('~', x + cardWidth / 2, y + 10.5, { align: 'center' });
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(24);
  pdf.setTextColor(255, 193, 7);
  pdf.text(`${data.partial_percentage}%`, x + cardWidth / 2, y + 20, { align: 'center' });
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(33, 37, 41);
  pdf.text('Partially Correct', x + cardWidth / 2, y + 25, { align: 'center' });
  pdf.setFontSize(8);
  pdf.setTextColor(108, 117, 125);
  pdf.text(`${data.total_partial.toLocaleString()} of ${data.total_questions_answered.toLocaleString()}`, x + cardWidth / 2, y + 30, { align: 'center' });
  y += cardHeight + 20;

  // Question Performance Section
  if (data.question_performance && data.question_performance.length > 0) {
    if (y > pageHeight - 80) {
      pdf.addPage();
      y = margin.top + 10;
    }

    pdf.setFillColor(248, 249, 251);
    pdf.rect(margin.left, y - 5, pageWidth - margin.left - margin.right, 8, 'F');
    
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.setTextColor(36, 67, 118);
    pdf.text('Performance by Question', margin.left + 3, y);
    y += 10;

    pdf.setFont('helvetica', 'italic');
    pdf.setFontSize(9);
    pdf.setTextColor(108, 117, 125);
    const descText = 'Questions are sorted by difficulty level. Red indicates difficult questions where trainees struggle most.';
    const descLines = pdf.splitTextToSize(descText, pageWidth - margin.left - margin.right);
    descLines.forEach((line: string, idx: number) => {
      pdf.text(line, margin.left, y + idx * 4);
    });
    y += descLines.length * 4 + 8;

    // Question performance table
    const tableData = data.question_performance.map((question: any) => {
      return [
        `Q${question.question_order}`,
        question.question_text.length > 40 ? question.question_text.substring(0, 37) + '...' : question.question_text,
        question.difficulty_level.charAt(0).toUpperCase() + question.difficulty_level.slice(1),
        question.total_answers.toString(),
        `${question.correct_percentage}%`,
        `${question.incorrect_percentage}%`,
        `${question.partial_percentage}%`,
        `${question.average_accuracy}%`
      ];
    });

    autoTable(pdf, {
      startY: y,
      head: [['Q#', 'Question', 'Difficulty', 'Total', 'Correct %', 'Wrong %', 'Partial %', 'Avg Accuracy']],
      body: tableData,
      theme: 'striped',
      headStyles: {
        fillColor: [36, 67, 118],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 10,
        halign: 'center'
      },
      bodyStyles: {
        fontSize: 9,
        textColor: [33, 37, 41],
        cellPadding: 3
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250]
      },
      columnStyles: {
        0: { cellWidth: 15, halign: 'center', fontStyle: 'bold' },
        1: { cellWidth: 60, halign: 'left' },
        2: { cellWidth: 25, halign: 'center', fontStyle: 'bold' },
        3: { cellWidth: 18, halign: 'center' },
        4: { cellWidth: 20, halign: 'center', textColor: [40, 167, 69], fontStyle: 'bold' },
        5: { cellWidth: 20, halign: 'center', textColor: [220, 53, 69], fontStyle: 'bold' },
        6: { cellWidth: 20, halign: 'center', textColor: [255, 193, 7], fontStyle: 'bold' },
        7: { cellWidth: 25, halign: 'center', fontStyle: 'bold', textColor: [36, 67, 118] }
      },
      margin: { left: margin.left, right: margin.right },
      didParseCell: (cellData: any) => {
        if (cellData.row.index > 0) {
          const question = data.question_performance[cellData.row.index - 1];
          
          if (question.difficulty_level === 'difficult') {
            cellData.cell.styles.fillColor = [255, 245, 245];
          } else if (question.difficulty_level === 'moderate') {
            cellData.cell.styles.fillColor = [255, 254, 245];
          } else {
            cellData.cell.styles.fillColor = [245, 255, 245];
          }
          
          if (cellData.column.index === 2) {
            if (question.difficulty_level === 'difficult') {
              cellData.cell.styles.textColor = [220, 53, 69];
            } else if (question.difficulty_level === 'moderate') {
              cellData.cell.styles.textColor = [255, 193, 7];
            } else {
              cellData.cell.styles.textColor = [40, 167, 69];
            }
          }
        }
      }
    });

    y = (pdf as any).lastAutoTable?.finalY || y;
    y += 15;

    // Key Insights Section
    if (y > pageHeight - 90) {
      pdf.addPage();
      y = margin.top + 10;
    }

    pdf.setFillColor(248, 249, 251);
    pdf.rect(margin.left, y - 5, pageWidth - margin.left - margin.right, 8, 'F');
    
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(16);
    pdf.setTextColor(36, 67, 118);
    pdf.text('Key Insights', margin.left + 3, y);
    y += 12;

    const difficultQuestions = data.question_performance
      .filter((q: any) => q.difficulty_level === 'difficult')
      .slice(0, 5);
    const easyQuestions = data.question_performance
      .filter((q: any) => q.difficulty_level === 'easy')
      .slice(0, 5);

    const insightsWidth = (pageWidth - margin.left - margin.right - 12) / 2;
    let insightsX = margin.left;
    const insightsHeight = Math.max(45, Math.max(difficultQuestions.length, easyQuestions.length) * 7 + 20);

    // Most Difficult Questions
    pdf.setFillColor(255, 245, 245);
    pdf.setDrawColor(220, 53, 69);
    pdf.setLineWidth(1);
    pdf.roundedRect(insightsX, y, insightsWidth, insightsHeight, 4, 4, 'FD');
    pdf.setFillColor(220, 53, 69);
    pdf.roundedRect(insightsX, y, insightsWidth, 10, 4, 4, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.setTextColor(255, 255, 255);
    pdf.text('Most Difficult Questions', insightsX + insightsWidth / 2, y + 6.5, { align: 'center' });
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(33, 37, 41);
    let insightsY = y + 18;
    if (difficultQuestions.length > 0) {
      difficultQuestions.forEach((question: any) => {
        pdf.setFillColor(220, 53, 69);
        pdf.circle(insightsX + 5, insightsY - 1, 1, 'F');
        pdf.text(`Q${question.question_order} - ${question.average_accuracy}% accuracy`, insightsX + 10, insightsY);
        insightsY += 7;
      });
    } else {
      pdf.setTextColor(108, 117, 125);
      pdf.text('No questions marked as difficult', insightsX + 5, insightsY);
    }
    insightsX += insightsWidth + 12;

    // Easiest Questions
    pdf.setFillColor(245, 255, 245);
    pdf.setDrawColor(40, 167, 69);
    pdf.roundedRect(insightsX, y, insightsWidth, insightsHeight, 4, 4, 'FD');
    pdf.setFillColor(40, 167, 69);
    pdf.roundedRect(insightsX, y, insightsWidth, 10, 4, 4, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.setTextColor(255, 255, 255);
    pdf.text('Easiest Questions', insightsX + insightsWidth / 2, y + 6.5, { align: 'center' });
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(33, 37, 41);
    insightsY = y + 18;
    if (easyQuestions.length > 0) {
      easyQuestions.forEach((question: any) => {
        pdf.setFillColor(40, 167, 69);
        pdf.circle(insightsX + 5, insightsY - 1, 1, 'F');
        pdf.text(`Q${question.question_order} - ${question.average_accuracy}% accuracy`, insightsX + 10, insightsY);
        insightsY += 7;
      });
    } else {
      pdf.setTextColor(108, 117, 125);
      pdf.text('No questions marked as easy', insightsX + 5, insightsY);
    }
  }

  // Professional Footer
  const pageCount = (pdf.internal as any).getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i);
    pdf.setDrawColor(220, 220, 220);
    pdf.setLineWidth(0.5);
    pdf.line(margin.left, pageHeight - 15, pageWidth - margin.right, pageHeight - 15);
    pdf.setFontSize(9);
    pdf.setTextColor(108, 117, 125);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Roslin Quiz System', margin.left, pageHeight - 8);
    pdf.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 8, { align: 'center' });
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(36, 67, 118);
    const footerText = reportName.length > 40 ? reportName.substring(0, 37) + '...' : reportName;
    pdf.text(footerText, pageWidth - margin.right, pageHeight - 8, { align: 'right' });
  }

  pdf.save(`${filename}.pdf`);
};

// Utility to get available export formats for a report type
export const getAvailableFormats = (reportType: string): string[] => {
  // All report types support JSON and CSV
  const formats = ['json', 'csv'];
  
  // Add PDF for supported report types
  if (['system_wide', 'quiz_summary', 'quiz_detailed', 'participant_performance', 'category_performance', 'overall_question_performance', 'quiz_question_performance'].includes(reportType)) {
    formats.push('pdf');
  }
  
  return formats;
};

export default exportReport;
